import React, { useState, useEffect, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import { Flex, Button, Upload } from "antd"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { ConfirmModal } from "ui/ConfirmModal"
import { spacings } from "themes/variables"
import { validateFile as validateFileUtil } from "utils/file-helpers"
import UploadArea from "./components/UploadArea"
import FilesList from "./components/FilesList"
import styles from "./index.module.css"
import { toaster } from "components/common/Toast"

// File upload configuration constants
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/jpeg",
  "image/jpg"
]

const MAX_FILE_SIZE_MB = 5
const UPLOAD_PROGRESS_INCREMENT = 10
const UPLOAD_PROGRESS_INTERVAL_MS = 100

/**
 * Validates file type and size using common utility
 * @param {File} file - The file to validate
 * @returns {{ isValid: boolean, error: string | null }}
 */
const validateFile = (file) => {
  const result = validateFileUtil(file, {
    acceptedTypes: ACCEPTED_FILE_TYPES,
    maxSizeMB: MAX_FILE_SIZE_MB
  })

  if (!result.isValid && result.error.includes("Invalid file type")) {
    return { isValid: false, error: "You can only upload DOC, DOCX, PDF, TXT, JPG files!" }
  }

  return result
}

/**
 * Unified LicensesModal component for adding and editing licenses and certifications
 */
const LicensesModal = ({ open, onCancel, onSave, mode = "add", initialFiles = [], loading = false }) => {
  const { t } = useTranslation()
  const uploadIntervalsRef = useRef({})
  const prevOpenRef = useRef(open)

  // Initialize files based on mode
  const getInitialFiles = useCallback(() => {
    if (mode === "edit" && initialFiles) {
      return initialFiles.map((file, index) => ({
        uid: file.uid || `${index}`,
        id: file.id, // Preserve ID for existing documents
        name: file.name,
        status: file.status || "done",
        percent: file.percent || 100,
        url: file.url // Preserve URL for existing documents
      }))
    }
    return []
  }, [mode, initialFiles])

  const [fileList, setFileList] = useState(getInitialFiles)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)

  // Update files only when modal transitions from closed to open
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFileList(getInitialFiles())
    }
    prevOpenRef.current = open
  }, [open, getInitialFiles])

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(uploadIntervalsRef.current).forEach((interval) => clearInterval(interval))
    }
  }, [])

  const handleBeforeUpload = (file) => {
    const { isValid, error } = validateFile(file)

    if (!isValid) {
      toaster.error(error)
      return Upload.LIST_IGNORE
    }

    return true
  }

  const handleCustomRequest = ({ file, onSuccess, onProgress }) => {
    const { isValid, error } = validateFile(file)

    if (!isValid) {
      console.error(error)
      return
    }

    // Simulate upload progress
    let progress = 0
    const interval = setInterval(() => {
      progress += UPLOAD_PROGRESS_INCREMENT
      onProgress({ percent: progress })

      if (progress >= 100) {
        clearInterval(interval)
        if (uploadIntervalsRef.current[file.uid]) {
          delete uploadIntervalsRef.current[file.uid]
        }
        onSuccess(file)
      }
    }, UPLOAD_PROGRESS_INTERVAL_MS)

    uploadIntervalsRef.current[file.uid] = interval
  }

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList)
  }

  const handleDeleteClick = (file) => {
    setFileToDelete(file)
    setIsDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!fileToDelete) return

    // Clear any ongoing upload interval for this file
    if (uploadIntervalsRef.current[fileToDelete.uid]) {
      clearInterval(uploadIntervalsRef.current[fileToDelete.uid])
      delete uploadIntervalsRef.current[fileToDelete.uid]
    }

    // If file has an ID (existing document), mark it for deletion instead of removing it
    if (fileToDelete.id) {
      const updatedFiles = fileList.map((file) => (file.uid === fileToDelete.uid ? { ...file, _destroy: true } : file))
      setFileList(updatedFiles)
    } else {
      // If it's a new upload without ID, just remove it from the list
      const updatedFiles = fileList.filter((file) => file.uid !== fileToDelete.uid)
      setFileList(updatedFiles)
    }

    setIsDeleteConfirmOpen(false)
    setFileToDelete(null)
  }

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false)
    setFileToDelete(null)
  }

  const handleSaveChanges = () => {
    // Prepare staff_documents_attributes array for API
    const staffDocumentsAttributes = []

    // Process each file in the list
    fileList.forEach((file) => {
      // Handle files marked for deletion
      if (file._destroy && file.id) {
        staffDocumentsAttributes.push({
          id: file.id,
          _destroy: true
        })
      }
      // Handle new files (no id, and upload completed)
      else if (!file.id && (file.status === "done" || file.percent === 100)) {
        staffDocumentsAttributes.push({
          file: file.originFileObj || file
        })
      }
    })

    // Pass the structured data to parent
    onSave(staffDocumentsAttributes)
  }

  const handleCancel = () => {
    if (mode === "add") {
      // Reset to initial blank state for add mode
      setFileList([])
    } else {
      // Revert to original data for edit mode
      setFileList(getInitialFiles())
    }
    onCancel()
  }

  const modalTitle = mode === "edit" ? t("crewMember.licenses.editTitle") : t("crewMember.licenses.addTitle")

  // Filter out files marked for deletion for display and validation
  const visibleFiles = fileList.filter((file) => !file._destroy)

  // Disable save if no files or if any files are still uploading or if saving
  const hasUploadingFiles = visibleFiles.some((file) => file.status === "uploading" || (file.percent && file.percent < 100))
  const isSaveDisabled = visibleFiles.length === 0 || hasUploadingFiles || loading

  return (
    <>
      <Modal open={open} onCancel={handleCancel} title={modalTitle} width={800} className={styles.modal} centered footer={null} closable={!loading} maskClosable={!loading}>
        <Flex vertical gap={spacings.XL} className={styles.modalContent}>
          {/* Description */}
          <p className={styles.description}>{t("crewMember.licenses.description")}</p>

          {/* Upload Section */}
          <Flex vertical gap={spacings.M}>
            <UploadArea fileList={fileList} onBeforeUpload={handleBeforeUpload} onChange={handleChange} customRequest={handleCustomRequest} />

            <FilesList files={visibleFiles} onDeleteClick={handleDeleteClick} />
          </Flex>

          {/* Footer Buttons */}
          <Flex gap={spacings.S} justify="flex-start" className={styles.modalFooter}>
            <Button onClick={handleCancel} variant="outlined" color="primary" size="large" disabled={loading} className={styles.cancelButton}>
              {t("crewMember.licenses.buttons.cancel")}
            </Button>
            <Button type="primary" onClick={handleSaveChanges} size="large" disabled={isSaveDisabled} loading={loading} className={styles.saveButton}>
              {mode === "edit" ? t("crewMember.licenses.buttons.saveChanges") : t("crewMember.licenses.buttons.save")}
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={isDeleteConfirmOpen}
        title={t("crewMember.licenses.deleteConfirmation.title")}
        info={t("crewMember.licenses.deleteConfirmation.message", {
          fileName: fileToDelete?.name || ""
        })}
        onCancel={handleCancelDelete}
        onDelete={handleConfirmDelete}
        cancelLabel={t("crewMember.licenses.buttons.cancel")}
        deleteLabel={t("crewMember.licenses.buttons.delete")}
      />
    </>
  )
}

LicensesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]),
  initialFiles: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string,
      id: PropTypes.number, // Document ID for existing documents
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      percent: PropTypes.number,
      url: PropTypes.string // File URL for existing documents
    })
  ),
  loading: PropTypes.bool
}

export default LicensesModal
